import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { RollSuggestion, Run } from './apg-copilot-domain';
import { capCards } from './apg-copilot-questionnaire';
import {
    DEFAULT_ORION_SORT_KEYS,
    nextSortDirection,
    OrionSortKeys,
    sortedByOrionKeys,
    typeLabelOf
} from './apg-roll-ranking';
import { DetectMethodChanges } from '../../utils';

// The results area: pending indicator, the sort toolbar, the ranked snippet cards and the explicit
// "no suggestions" empty state. Presentational — forwards preview/review intents up.
@Component({
    selector: 'ets-apg-copilot-results-list',
    templateUrl: './apg-copilot-results-list.component.html',
    styleUrls: ['./apg-copilot-results-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApgCopilotResultsListComponent {

    constructor(private readonly _changeDetector: ChangeDetectorRef) { }

    @Input() run: Run | null = null;

    @Input()
    set suggestions(value: RollSuggestion[]) {
        this._suggestions = value || [];
        // A NEW RESULT SET IS A NEW QUESTION, so it comes back on the owner's default order — best
        // per-day credit first. The keys themselves are NOT reset: a trader who has chosen to read
        // by Type keeps reading by Type across runs, which is the whole point of the control being
        // his rather than the panel's.
        this._ordered = null;
    }
    get suggestions(): RollSuggestion[] {
        return this._suggestions;
    }

    /**
     * "Show top N results" — THE DISPLAY LIMIT (CR-20260910-001, ruling R-B).
     *
     * ⚠ IT IS A LIMIT ON WHAT IS SHOWN, NOT ON WHAT WAS CALCULATED, and that distinction is the
     * whole of the ruling. The run delivers EVERY window date it priced, because the Price Box now
     * feeds its columns from that same delivery — a backend cut would blank the columns of every
     * date it dropped. So the setting is applied HERE, at render, after the trader's own sort, and
     * changing it re-renders without re-running.
     *
     * null / absent = the MAX_ROLL_CARDS default, which is what `capCards` already means.
     */
    @Input()
    set maxCards(value: number | null) {
        this._maxCards = value;
        this._ordered = null;          // a new limit is a new render, not a new question
    }
    get maxCards(): number | null {
        return this._maxCards;
    }

    @Output() previewToggle = new EventEmitter<RollSuggestion>();
    @Output() review = new EventEmitter<RollSuggestion>();

    private _suggestions: RollSuggestion[] = [];
    private _maxCards: number | null = null;
    private _ordered: RollSuggestion[] | null = null;
    private _sortKeys: OrionSortKeys = {
        type: DEFAULT_ORION_SORT_KEYS.type,
        price: DEFAULT_ORION_SORT_KEYS.price
    };

    // ---- the sort control (owner ruling O47/O50/O59, 2026-09-07) --------------------------------
    //
    // ONE-TIME SORT, THEN HIS CLICK. The owner: "Once we receive the prices for every one of them, we
    // do a single, one-time sort according to that label direction… if prices change, the user can
    // manually re-click that sorting label, which will re-sort."
    //
    // ⚠ THE ORDER NEVER MOVES BY ITSELF. The list is memoised against the suggestion array, so a
    // change-detection pass driven by a ticking quote re-renders the SAME order. The money on a card
    // is live; the ORDER is not. Re-sorting under a reader's cursor is precisely what he ruled out,
    // and it would be invisible in a test that only asserts set membership.
    //
    // Nothing here re-derives the ordering figure: it sorts on `perDayCredit`, the exact number the
    // run ranked on, through the SAME pure comparator the skill used.

    get sortKeys(): OrionSortKeys {
        return this._sortKeys;
    }

    get orderedSuggestions(): RollSuggestion[] {
        if (this._ordered === null) {
            this._ordered = this.applySort();
        }
        return this._ordered;
    }

    /**
     * The arrow. No glyph for 'none' — an absent arrow IS the state, and a third glyph for "not
     * sorting by this" reads as a third sort mode.
     */
    sortArrowClass(key: 'type' | 'price'): string | null {
        const dir = this._sortKeys[key];
        if (dir === 'desc') {
            return 'fas fa-caret-down';
        }
        if (dir === 'asc') {
            return 'fas fa-caret-up';
        }
        return null;
    }

    /**
     * ⚠ `@DetectMethodChanges()`, NOT `markForCheck()` — and the difference is the whole bug.
     *
     * WHAT THE OWNER HIT (2026-09-07): clicking a sort label did nothing at all until he moved the
     * window or touched something else, and then the list appeared in its new order. The state had
     * changed on the first click; only the paint was missing.
     *
     * CAUSE. `markForCheck()` marks this view and its ancestors dirty — it does NOT run change
     * detection. It relies on a tick happening anyway, which for a DOM event means the click being
     * handled inside Angular's zone. This panel does not get that reliably, so the flag sat there
     * until the next unrelated event ticked and swept it up. Every symptom follows: the right order,
     * the wrong moment.
     *
     * The decorator is the codebase's own answer (the Hedge Matrix's identical control uses it): it
     * calls `detectChanges()` on `this._changeDetector` after the method returns, which is why that
     * field is named exactly that and read by string.
     */
    @DetectMethodChanges()
    onSortKeyClick(key: 'type' | 'price'): void {
        this._sortKeys = {
            type: key === 'type' ? nextSortDirection('type', this._sortKeys.type) : this._sortKeys.type,
            price: key === 'price' ? nextSortDirection('price', this._sortKeys.price) : this._sortKeys.price
        };
        this._ordered = this.applySort();
    }

    // SORT THE WHOLE SET, THEN CUT. In that order, always: cutting first would show the top N of an
    // arbitrary order and then sort them, so the trader would be reading "the best five of some
    // five" rather than the best five. The delivered set is every calculated date (R-B), so the cut
    // is the only thing standing between him and all of them.
    private applySort(): RollSuggestion[] {
        const ordered = sortedByOrionKeys(
            this._suggestions,
            this._sortKeys,
            s => typeLabelOf(s.content ? s.content.adjustmentTypes : []),
            s => (s.content ? s.content.perDayCredit : undefined),
            s => (s.content ? s.content.daysOut : 0));
        return capCards(ordered, this._maxCards);
    }

    get isPending(): boolean {
        return !!this.run && this.run.outcome === 'pending';
    }

    get isEmpty(): boolean {
        return !!this.run && this.run.outcome === 'empty';
    }

    get isFailed(): boolean {
        return !!this.run && this.run.outcome === 'terminalFailed';
    }

    get hasCards(): boolean {
        return this.suggestions && this.suggestions.length > 0;
    }

    // ---- What survives of the note surface (CR-20260910-001, ruling R-C) ---------------------
    //
    // ⚠ WHAT WAS HERE, AND WHY IT IS GONE. Four getters and a `datesClause` helper composed the
    // COVERAGE NOTE: the calendar could not vouch for N days, N days were not priced in time, N days
    // were filtered out by your settings, N days have no days left to run. Ruling R-C removed all of
    // it — "no coverage note, no counts, no reported dropped, excluded, filtered or unpriced days or
    // reasons".
    //
    // THIS IS NOT THE OLD FAILURE RETURNING. The facts are still recorded: the backend writes every
    // window date without a card to its own log with the reason (contract A1 guarantee A-G5). What
    // changed is who reads them — support, not the trader. And the two things that actually answered
    // "why am I looking at nothing" are still on screen: the `empty` state's own sentence above, and
    // the warnings below.
    //
    // A warning is NOT a dropped-day report, which is why R-C leaves it standing: `ZoneUnavailable`
    // says the run priced BOTH adjustments because the portfolio has no zone. That is a statement
    // about the basis of every card shown, not about a day that is missing.

    get warningNotes(): string[] {
        return (this.run && this.run.warnings) ? this.run.warnings : [];
    }

    get hasWarningNotes(): boolean {
        return this.warningNotes.length > 0;
    }
}
